import { StateCreator } from 'zustand';
import { SessionState, MarkdownEditorOpenFile } from './session/state';

const RECENT_FILES_LIMIT = 15;

export interface MarkdownEditorSlice {
  markdownEditorOpenFiles: MarkdownEditorOpenFile[];
  markdownEditorActiveFile: string | null;
  markdownEditorTreeState: Record<string, boolean>;
  markdownEditorSearchTerm: string;
  markdownEditorRecentFiles: string[];
  openMarkdownEditorFile: (file: MarkdownEditorOpenFile) => void;
  closeMarkdownEditorFile: (path: string) => void;
  setMarkdownEditorActiveFile: (path: string | null) => void;
  setMarkdownEditorTreeState: (updater: (prevState: Record<string, boolean>) => Record<string, boolean>) => void;
  setMarkdownEditorSearchTerm: (term: string) => void;
  setMarkdownEditorOpenFiles: (files: MarkdownEditorOpenFile[]) => void;
  closeAllMarkdownEditorFiles: () => void;
  closeOtherMarkdownEditorFiles: (path:string) => void;
  closeRightMarkdownEditorFiles: (path: string) => void;
}

const getParentPaths = (path: string): string[] => {
  const parts = path.split('/');
  if (parts.length <= 1) {
    return [];
  }
  const parents: string[] = [];
  for (let i = 1; i < parts.length; i++) {
    parents.push(parts.slice(0, i).join('/'));
  }
  return parents;
};

const updateRecentFiles = (path: string, currentRecentFiles: string[]): string[] => {
  const filteredRecents = currentRecentFiles.filter(p => p !== path);
  const newRecents = [path, ...filteredRecents];
  return newRecents.slice(0, RECENT_FILES_LIMIT);
};

export const createMarkdownEditorSlice: StateCreator<
  SessionState,
  [],
  [],
  MarkdownEditorSlice
> = (set, get) => ({
  markdownEditorOpenFiles: [],
  markdownEditorActiveFile: null,
  markdownEditorTreeState: { '.': true },
  markdownEditorSearchTerm: '',
  markdownEditorRecentFiles: [],

  openMarkdownEditorFile: (file) => {
    const { markdownEditorOpenFiles, markdownEditorTreeState, markdownEditorRecentFiles } = get();
    const existingFileIndex = markdownEditorOpenFiles.findIndex(
      (f) => f.path === file.path,
    );

    let newOpenFiles;
    if (existingFileIndex !== -1) {
      // File exists, update its content
      newOpenFiles = [...markdownEditorOpenFiles];
      newOpenFiles[existingFileIndex] = file;
    } else {
      // File doesn't exist, add it
      newOpenFiles = [...markdownEditorOpenFiles, file];
    }
    
    const parentPaths = getParentPaths(file.path);
    const newTreeState = { ...markdownEditorTreeState };
    parentPaths.forEach((p) => {
      newTreeState[p] = true;
    });

    const newRecentFiles = updateRecentFiles(file.path, markdownEditorRecentFiles);

    set({
      markdownEditorOpenFiles: newOpenFiles,
      markdownEditorActiveFile: file.path,
      markdownEditorTreeState: newTreeState,
      markdownEditorRecentFiles: newRecentFiles,
    });
  },

  closeMarkdownEditorFile: (path) => {
    const { markdownEditorOpenFiles, markdownEditorActiveFile } = get();
    const newOpenFiles = markdownEditorOpenFiles.filter((f) => f.path !== path);

    let newActiveFile = markdownEditorActiveFile;
    if (markdownEditorActiveFile === path) {
      if (newOpenFiles.length > 0) {
        const closedFileIndex = markdownEditorOpenFiles.findIndex(
          (f) => f.path === path,
        );
        const newActiveIndex = Math.max(0, closedFileIndex - 1);
        newActiveFile = newOpenFiles[newActiveIndex].path;
      } else {
        newActiveFile = null;
      }
    }

    set({
      markdownEditorOpenFiles: newOpenFiles,
      markdownEditorActiveFile: newActiveFile,
    });
  },

  setMarkdownEditorActiveFile: (path) => {
    if (path) {
      const { markdownEditorTreeState, markdownEditorRecentFiles } = get();
      
      const parentPaths = getParentPaths(path);
      const newTreeState = { ...markdownEditorTreeState };
      parentPaths.forEach((p) => {
        newTreeState[p] = true;
      });

      const newRecentFiles = updateRecentFiles(path, markdownEditorRecentFiles);
      
      set({
        markdownEditorActiveFile: path,
        markdownEditorTreeState: newTreeState,
        markdownEditorRecentFiles: newRecentFiles,
      });
    } else {
      set({ markdownEditorActiveFile: null });
    }
  },

  setMarkdownEditorTreeState: (updater) => {
    set((state) => ({
      markdownEditorTreeState: updater(state.markdownEditorTreeState),
    }));
  },

  setMarkdownEditorSearchTerm: (term) => {
    set({ markdownEditorSearchTerm: term });
  },

  setMarkdownEditorOpenFiles: (files) => {
    set({ markdownEditorOpenFiles: files });
  },
  
  closeAllMarkdownEditorFiles: () => {
    set({
      markdownEditorOpenFiles: [],
      markdownEditorActiveFile: null,
    });
  },

  closeOtherMarkdownEditorFiles: (path) => {
    const { markdownEditorOpenFiles } = get();
    const fileToKeep = markdownEditorOpenFiles.find((f) => f.path === path);
    if (fileToKeep) {
      set({
        markdownEditorOpenFiles: [fileToKeep],
        markdownEditorActiveFile: path,
      });
    }
  },
  
  closeRightMarkdownEditorFiles: (path) => {
    const { markdownEditorOpenFiles, markdownEditorActiveFile } = get();
    const fileIndex = markdownEditorOpenFiles.findIndex((f) => f.path === path);

    if (fileIndex === -1) {
      return; // File not found, do nothing
    }

    const newOpenFiles = markdownEditorOpenFiles.slice(0, fileIndex + 1);

    // Check if the currently active file is still present in the new list
    const activeFileStillOpen = newOpenFiles.some(
      (f) => f.path === markdownEditorActiveFile,
    );

    set({
      markdownEditorOpenFiles: newOpenFiles,
      // If the active file was closed, make the target file the new active one.
      markdownEditorActiveFile: activeFileStillOpen
        ? markdownEditorActiveFile
        : path,
    });
  },
});